import {
  type ComponentPropsWithRef,
  type HTMLAttributes,
  type ReactNode,
  type SubmitEventHandler,
  useCallback,
} from 'react'
import cx from 'classnames'
import { ContentLabel } from '@/components/ContentLabel'
import styles from './Form.module.css'

export interface FormProps extends Omit<
  ComponentPropsWithRef<'form'>,
  'onSubmit'
> {
  /**
   * Form submit handler.
   */
  onSubmit?: SubmitEventHandler<HTMLFormElement>

  /**
   * Whether pressing Enter is prevented from submitting the form, such as
   * while a submission is pending.
   */
  isSubmitDisabled?: boolean

  /**
   * Test ID attribute.
   */
  testId?: string
}

/**
 * The Form itself.
 */
export function Form({
  ref,
  action = '#', // makes sure Safari shows a `go` button on the keyboard rather than `return`
  onSubmit,
  isSubmitDisabled,
  testId,
  children,
  ...props
}: FormProps) {
  const submit: SubmitEventHandler<HTMLFormElement> = useCallback(
    (e) => {
      // Without an `onSubmit`, Enter in a lone text input would still navigate to `action`.
      e.preventDefault()
      onSubmit?.(e)
    },
    [onSubmit],
  )

  return (
    <form
      {...props}
      ref={ref}
      action={action}
      onSubmit={submit}
      data-test={testId}
    >
      {children}
      {/* Lets Enter submit forms whose submit button sits outside the form or is absent. */}
      {onSubmit && <input type="submit" hidden disabled={isSubmitDisabled} />}
    </form>
  )
}

export interface FormGroupProps {
  label?: string
  sublabel?: string
  children?: ReactNode
  className?: string
  title?: string
  htmlFor?: string
  testId?: string
}

/**
 * A labelled group of form controls.
 */
export function FormGroup({
  children,
  className,
  label,
  sublabel,
  title,
  htmlFor,
  testId,
}: FormGroupProps) {
  return (
    <div className={className} data-test={testId}>
      {label && (
        <label title={title} className={styles.groupLabel} htmlFor={htmlFor}>
          <>
            {label === ' ' ? <>&nbsp;</> : label}
            {sublabel && (
              <>
                &nbsp;<span className={styles.groupSublabel}>{sublabel}</span>
              </>
            )}
          </>
        </label>
      )}
      {children}
    </div>
  )
}

export type FormRowProps = HTMLAttributes<HTMLDivElement>

/**
 * A row of form groups, spaced from the next row.
 */
export function FormRow({ children, className, ...rest }: FormRowProps) {
  return (
    <div className={cx(styles.row, className)} {...rest}>
      {children}
    </div>
  )
}

interface FormColumnsProps {
  children?: ReactNode
}

/**
 * Lays out {@link FormColumn}s side by side, stacking them on tablets.
 */
export function FormColumns({ children }: FormColumnsProps) {
  return <div className={styles.columns}>{children}</div>
}

interface FormColumnProps {
  children?: ReactNode
}

/**
 * A column in {@link FormColumns}.
 */
export function FormColumn({ children }: FormColumnProps) {
  return <div className={styles.column}>{children}</div>
}

interface FormActionsProps {
  children?: ReactNode
}

/**
 * The form's action buttons, aligned to the end.
 */
export function FormActions({ children }: FormActionsProps) {
  return (
    <div className={styles.actions}>
      <div>{children}</div>
    </div>
  )
}

interface FormSectionProps {
  edgeToEdge?: boolean
  children?: ReactNode
  /**
   * If this is a `String`, will wrap in a <ContentLabel>. Otherwise renders as-is.
   */
  label?: ReactNode
}

/**
 * A labelled, bordered section of a form.
 */
export function FormSection({ children, edgeToEdge, label }: FormSectionProps) {
  return (
    <div className={cx(styles.section, edgeToEdge && styles.edgeToEdge)}>
      <div className={styles.sectionLabel}>
        {typeof label === 'string' ? (
          <ContentLabel>{label}</ContentLabel>
        ) : (
          label
        )}
      </div>

      <div className={styles.sectionBox}>{children}</div>
    </div>
  )
}

interface HelpProps extends HTMLAttributes<HTMLSpanElement> {
  help?: ReactNode
  error?: string
}

/**
 * Help text for a control; an `error` takes its place and turns it red.
 */
export function Help({ error, help, className, ...props }: HelpProps) {
  return error || help ? (
    <span
      className={cx(styles.help, className, error && styles.helpDanger)}
      {...props}
    >
      {error || help}
    </span>
  ) : null
}
